import { Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

import { CountriesVm } from '@booking/entities/countries';
import { UserAddresses } from '@app/modules/core/interfaces/address';
import { User } from '@app/modules/core/interfaces/user';
import {
  Address, BillingCity, BillingCreateItem, BillingPersonalInfo, BillingResponse, BillingVm
} from '@billing/entities/billing.payload';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BookingBillingTypeDocumentProps, BusinessRulesByBookingType } from '@app/entities/booking-documents';
import { FormDocumentInputComponent } from '@forms/form-components/components/form-document-input/form-document-input.component';
import { AddressFormContainer } from '@billing/containers/address-form/address-form.container';
import { PERSON_ADDRESS_FORM_INPUTS, PERSON_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FormInputType } from '@forms/form-text-input/entities/input-type';
import { BillingFormService } from '@billing/services/billing-form.service';
import { BillingDocumentsPipe } from '@billing/pipes/billing-documents.pipe';
import { BillingPersonType } from '@booking/entities/billing-person-types';
import { environment as ENV } from '@environment';
import { Countries } from '@ux/entities/country.enum';
import { FormValidationService } from '@reservation/services/form-validation.service';

@Directive()
export abstract class BillingTypeFormAbstract {

  @Input() bookingForm: UntypedFormGroup;
  @Input() form: UntypedFormGroup;
  @Input() user: User;
  @Input() countries: CountriesVm[] = [];
  @Input() cities: BillingCity[] = [];
  @Input() userAddresses: UserAddresses;
  @Input() businessRules: BusinessRulesByBookingType;
  @Input() bookingType: TravelBookingType;
  @Input() withErrorLaunch: boolean;
  @Input() isMobile = false;

  @Input() set isVisibleParentNode(visible: boolean) {
    this._isVisibleParentNode = !!visible;
    if (!this.isVisibleParentNode) {
      this.selectedCityCode = null;
      this._country = null;
    }
  }

  @Output() update: EventEmitter<BillingVm> = new EventEmitter();
  @Output() create: EventEmitter<BillingCreateItem> = new EventEmitter();

  @ViewChild('legalName') public legalNameRef: ElementRef;
  @ViewChild('firstName') public firstNameRef: ElementRef;
  @ViewChild('secondName') public secondNameRef: ElementRef;
  @ViewChild('lastName') public lastNameRef: ElementRef;
  @ViewChild('documentNumber') public documentNumberRef: ElementRef;
  @ViewChild('email') public emailRef: ElementRef;

  @ViewChild('documentSelectorRef', { read: FormDocumentInputComponent }) public documentSelectorRef: FormDocumentInputComponent;
  @ViewChild('addressFormRef', { read: AddressFormContainer }) public addressFormRef: AddressFormContainer;

  public documentNumberSelector: string = PERSON_FORM_INPUTS.DOCUMENT_NUMBER;
  public configurationDocumentId: string = PERSON_FORM_INPUTS.DOCUMENT_ID;
  public companyName: string = PERSON_FORM_INPUTS.LEGAL_NAME;
  public firstNameControl: string = PERSON_FORM_INPUTS.FIRST_NAME;
  public middleNameControl: string = PERSON_FORM_INPUTS.SECOND_NAME;
  public lastNameControl: string = PERSON_FORM_INPUTS.LAST_NAME;

  public onlyLettersInput: FormInputType = FormInputType.ONLY_LETTERS;
  public emailInputType: FormInputType = FormInputType.EMAIL;

  public saveFormData: boolean = true;
  public disabledCountryOptions: boolean = false;
  public selectedCityCode: string = null;

  protected _activeForm: boolean;
  protected _isVisibleParentNode: boolean;

  protected defaultFormData: BillingPersonalInfo = {} as BillingPersonalInfo;
  protected defaultAddressFormData: Address = {} as Address;

  private _isSubmitted: boolean = false;
  public _country: string = null;

  constructor(
    protected billingFormService: BillingFormService,
    protected billingDocumentsPipe: BillingDocumentsPipe,
    protected formValidationService: FormValidationService,
  ) { }

  get isVisibleParentNode(): boolean {
    return this._isVisibleParentNode;
  }

  get isSubmitted(): boolean {
    return (this.withErrorLaunch && !this._isSubmitted) ? !!this.withErrorLaunch : !!this._isSubmitted;
  }

  get hasUserInfo(): boolean {
    return this.billingFormService.hasUserInfo(this.user);
  }

  get isBr(): boolean {
    return ENV.countryInfo.iso_code === Countries.Brazil;
  }

  set isSubmitted(value: boolean) {
    this._isSubmitted = value;
  }

  public changeUpperCaseName(event: Event, inputType: string, form: UntypedFormGroup): void {
    this.billingFormService.changeUpperCasePersonNames(form, event.target['value'], inputType);
  }

  public setSaveFormData(): void {
    this.saveFormData = !this.saveFormData;
  }

  public scroll(elementRef: ElementRef): void {
    this.billingFormService.scrollIntoView(elementRef);
  }

  public selectedCityAbstract(code: string, formType: UntypedFormGroup, addressForm: UntypedFormGroup): void {
    this.selectedCityCode = code;
    addressForm.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]: this.selectedCityCode });
    formType.patchValue({ [PERSON_FORM_INPUTS.ADDRESS]: addressForm.value });
  }

  protected documentsByType$(
    type: BillingPersonType, countryCodeByDocuments: string
  ): Observable<BookingBillingTypeDocumentProps[]> {
    return this.billingDocumentsPipe.transform(type, countryCodeByDocuments);
  }

  protected hasDefaultBillingInfoAbstract(defaultBilling: BillingResponse): boolean {
    return this.billingFormService.hasDefaultBillingInfo(defaultBilling);
  }

  protected validateFiledsAbstract(form: UntypedFormGroup, refs: { [key: string]: ElementRef } = {}): void {
    this.billingFormService.validateSectionFormToScroll(form, refs);
  }

  protected defaultAddressFormDataAbstract(defaultBilling: BillingResponse): Address {
    return this.billingFormService.defaultBillingPersonAddress(
      this.userAddresses, defaultBilling, this.user, this.cities, this.countries
    );
  }

  protected addressFormAbstract(formType: UntypedFormGroup): UntypedFormGroup {
    return formType ? formType.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup : null;
  }

  protected addressFormFields(): UntypedFormGroup {
    return new UntypedFormGroup({
      [PERSON_ADDRESS_FORM_INPUTS.STREET]: new UntypedFormControl(this.defaultAddressFormData.street,
        this.formValidationService.addressValidator),
      [PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]: new UntypedFormControl(this.defaultAddressFormData.city_code,
        this.billingFormService.stringAddresCodeValidators),
      [PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE]: new UntypedFormControl(this.defaultAddressFormData.country_code,
        this.billingFormService.stringAddresCodeValidators),
      [PERSON_ADDRESS_FORM_INPUTS.STATE_CODE]: new UntypedFormControl(this.defaultAddressFormData.state_code,
        this.billingFormService.stringAddresCodeValidators),
      [PERSON_ADDRESS_FORM_INPUTS.NEIGHBORHOOD]: new UntypedFormControl(this.defaultAddressFormData.neighborhood,
        this.isBr ? this.billingFormService.stringAddresCodeValidators : null),
      [PERSON_ADDRESS_FORM_INPUTS.POSTAL_CODE]: new UntypedFormControl(this.defaultAddressFormData.postal_code,
        this.isBr ? this.billingFormService.stringAddresCodeValidators : null),
      [PERSON_ADDRESS_FORM_INPUTS.COMPLEMENT]: new UntypedFormControl(this.defaultAddressFormData.complement),
    })
  }
}

